import { Download, Edit2, Plus, Trash2, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import DashboardFilterBar, {
  ALL_OPTION,
  isAllFilter,
  stringFromFilter,
} from "../../components/ui/dashboard/DashboardFilterBar";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { buildApiUrl, formatDate } from "../../lib/utils";
import {
  IncidentReport,
  IncidentReportSearchRequest,
  IncidentReportSearchResponse,
} from "./@types";
import { useGetIncidentReportsMutation } from "./incidentReportApiSlice";
import IncidentReportModal from "./IncidentReportModal";
import IncidentReportDashStats from "./refactor/IncidentReportDashStats";

const REPORT_TYPE_OPTIONS = [
  ALL_OPTION,
  { value: "Incident", label: "Incident" },
  { value: "Accident", label: "Accident" },
];

const SEVERITY_OPTIONS = [
  ALL_OPTION,
  { value: "Minor", label: "Minor" },
  { value: "Bad", label: "Bad" },
  { value: "Critical", label: "Critical" },
];

const IncidentReportDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [reportTypeFilter, setReportTypeFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedIncidentReport, setSelectedIncidentReport] =
    useState<IncidentReport | null>(null);
  const [openModal, setOpenModal] = useState("");
  const debouncedKeyword = useDebouncedValue(searchTerm.trim());

  const [getIncidentReport, { data: maintenanceData, isLoading, error: loadError }] =
    useGetIncidentReportsMutation();

  const fetchIR = useCallback(async () => {
    const requestBody: IncidentReportSearchRequest = {
      pageNumber: currentPage,
      pageSize,
      keyword: debouncedKeyword || undefined,
      reportType: stringFromFilter(reportTypeFilter),
      severity: stringFromFilter(severityFilter),
    };

    try {
      await getIncidentReport(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch incident reports:", err);
    }
  }, [
    currentPage,
    pageSize,
    debouncedKeyword,
    reportTypeFilter,
    severityFilter,
    getIncidentReport,
  ]);

  useEffect(() => {
    fetchIR();
  }, [fetchIR]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedKeyword, reportTypeFilter, severityFilter]);

  // Table columns configuration
  const columns: Column<IncidentReport>[] = [
    {
      key: "vehicleModel",
      header: "Vehicle",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "driverName",
      header: "Driver",
      width: "",
      render: (value: string, item: IncidentReport) => (
        <div className="flex items-center space-x-3">
          {item.driverImgUrl ? (
            <img
              src={buildApiUrl(item.driverImgUrl)}
              alt={`${item.driverName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.driverName}</span>
          </div>
        </div>
      ),
    },
    {
      key: "location",
      header: "Location",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "reportType",
      header: "IncidentReport type",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "createdOn",
      header: "Date",
      width: "20",
      render: (value: string) => (
        <span className="text-hColor">{formatDate(value)}</span>
      ),
    },
    {
      key: "severity",
      header: "Status",
      width: "20",
      render: (value: boolean, item: IncidentReport) => {
        const getStatusInfo = () => {
          if (!value)
            return {
              label: "Minor",
              color: "text-green-200 border border-green-200",
            };
          if (item.severity === "Critical")
            return {
              label: "Critical",
              color: "text-red-800 border border-red-800",
            };
          if (item.severity === "Bad")
            return {
              label: "Bad",
              color: "text-yellow-800 border border-yellow-800",
            };
          return {
            label: "Critical",
            color: "text-red-800 border border-red-800",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`w-[60px] inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Action",
      width: "12",
      align: "center" as const,
      render: (value: any, item: IncidentReport) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "Edit",
                onClick: () => {},
                icon: <Edit2 className="w-4 h-4" />,
              },
              {
                label: "Delete",
                onClick: () => {
                  setSelectedIncidentReport(item);
                  setOpenModal("delete");
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleExportCSV = () => {
    console.log("Export CSV functionality");
  };

  const handleModal = () => {
    setOpenModal("");
    setSelectedIncidentReport(null);
    fetchIR();
  };

  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (debouncedKeyword) {
      chips.push({
        key: "keyword",
        label: `Search: ${debouncedKeyword}`,
        onRemove: () => setSearchTerm(""),
      });
    }
    if (!isAllFilter(reportTypeFilter)) {
      chips.push({
        key: "type",
        label: `Type: ${reportTypeFilter}`,
        onRemove: () => setReportTypeFilter("all"),
      });
    }
    if (!isAllFilter(severityFilter)) {
      chips.push({
        key: "severity",
        label: `Severity: ${severityFilter}`,
        onRemove: () => setSeverityFilter("all"),
      });
    }
    return chips;
  }, [debouncedKeyword, reportTypeFilter, severityFilter]);

  const response = maintenanceData?.data as
    | IncidentReportSearchResponse
    | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Dashboard Cards */}
        <div className="">
          <IncidentReportDashStats />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">
              All incident reports
            </h2>
          </div>

          <div className="flex gap-3">
            <AppButton
              appearance="soft"
              onClick={handleExportCSV}
              className="flex items-center px-4 py-2 border border-primary bg-white rounded-lg text-primary hover:bg-gray-50 transition-colors min-w-[146px] h-[38px]"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </AppButton>
            <AppButton
              onClick={() => setOpenModal("add")}
              className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors h-[38px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add incident
            </AppButton>
          </div>
        </div>

        {/* All incident reports section */}
        <div className="space-y-4 bg-white rounded-[10px] p-4">
          <DashboardFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={() => setSearchTerm("")}
            searchPlaceholder="Search by vehicle, driver or location"
            isLoading={isLoading}
            filters={[
              {
                id: "report-type",
                placeholder: "Report type",
                value: reportTypeFilter,
                options: REPORT_TYPE_OPTIONS,
                onChange: (value) => setReportTypeFilter(value || "all"),
              },
              {
                id: "severity",
                placeholder: "Severity",
                value: severityFilter,
                options: SEVERITY_OPTIONS,
                onChange: (value) => setSeverityFilter(value || "all"),
              },
            ]}
            chips={activeFilters}
            onClearAll={() => {
              setSearchTerm("");
              setReportTypeFilter("all");
              setSeverityFilter("all");
            }}
            resultCount={response?.totalCount}
            resultLabel={response?.totalCount === 1 ? "report" : "reports"}
          />

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              error={loadError}
              emptyMessage="No incident reports found"
            />

            {/* Pagination */}
            {response && (
              <Pagination
                currentPage={response.currentPage}
                totalPages={response.totalPages}
                pageSize={response.pageSize}
                totalCount={response.totalCount}
                hasNextPage={response.hasNextPage}
                hasPreviousPage={response.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>
      </div>

      <IncidentReportModal
        isOpen={openModal === "add" || openModal === "edit"}
        onClose={() => handleModal()}
        incidentReport={selectedIncidentReport ? selectedIncidentReport : null}
      />
    </div>
  );
};

export default IncidentReportDashboard;
